import React, { useId, useState } from 'react';
import { REPORT_REASONS, reportErrorMessage, reportMedia } from '../reportsApi';
import useDialogFocus from '../hooks/useDialogFocus';

export default function ReportMediaDialog({ galleryId, item, onClose }) {
  const titleId = useId();
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [contact, setContact] = useState('');
  const [pending, setPending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const close = () => { if (!pending) onClose(); };
  const dialogRef = useDialogFocus(true, close);
  const inputClass = 'w-full min-h-[44px] rounded-lg border border-accent bg-surface px-3 py-3 text-secondary focus:outline-none focus:ring-2 focus:ring-primary-50';

  const submit = async (event) => {
    event.preventDefault();
    if (!reason || pending || submitted) return;
    setPending(true);
    setError('');
    try {
      await reportMedia(galleryId, {
        mediaId: item.id,
        reason,
        ...(details.trim() ? { details: details.trim() } : {}),
        ...(contact.trim() ? { contact: contact.trim() } : {}),
      });
      setSubmitted(true);
    } catch (failure) {
      setError(reportErrorMessage(failure));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-60 px-4 py-6 backdrop-blur-sm" onClick={(event) => { if (event.target === event.currentTarget) close(); }}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="w-full max-w-md overflow-y-auto rounded-2xl border border-accent-40 bg-surface p-6 text-secondary shadow-xl" style={{ maxHeight: '90vh' }}>
        <h2 id={titleId} className="mb-2 text-xl font-semibold">Report content</h2>
        {submitted ? (
          <>
            <div role="status" className="space-y-3 text-sm">
              <p>Your report has been submitted. We review reports within 24 hours.</p>
              <p>We will contact you about the outcome if you provided contact details.</p>
            </div>
            <button type="button" onClick={close} className="mt-5 min-h-[44px] rounded-lg bg-primary px-4 py-3 font-semibold text-white">Close</button>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-4 text-sm">
            <p>Tell us why this photo or video should be reviewed. We review reports within 24 hours.</p>
            <label className="block">
              <span className="mb-2 block font-medium">Reason</span>
              <select required value={reason} onChange={(event) => setReason(event.target.value)} disabled={pending} className={inputClass}>
                <option value="">Choose a reason</option>
                {REPORT_REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-2 block font-medium">Details (optional)</span>
              <textarea rows={4} maxLength={2000} value={details} onChange={(event) => setDetails(event.target.value)} disabled={pending} className={inputClass} />
            </label>
            <label className="block">
              <span className="mb-2 block font-medium">Email or WhatsApp (optional)</span>
              <input type="text" maxLength={254} value={contact} onChange={(event) => setContact(event.target.value)} disabled={pending} className={inputClass} />
            </label>
            <p className="text-xs">Your contact details are shared with our support team to follow up on this report.</p>
            {error && <p role="alert" className="text-red-700">{error}</p>}
            <div className="flex flex-wrap justify-end gap-3 pt-2">
              <button type="button" disabled={pending} onClick={close} className="min-h-[44px] rounded-lg border border-accent px-4 py-3 font-semibold">Cancel</button>
              <button type="submit" disabled={!reason || pending} className="min-h-[44px] rounded-lg bg-primary px-4 py-3 font-semibold text-white disabled:opacity-50">{pending ? 'Sending…' : 'Send report'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
